import { useState, useEffect } from 'react';
import { searchPets } from '../services/petfinderService';
import { loadLocationPreferences } from '../services/locationService';

/**
 * Generate demo pets when API is not available
 */
const generateDemoPets = (quizResponses, breedPreference) => {
  const basePets = [
    {
      id: 'demo-1',
      name: 'Buddy',
      type: 'dog',
      species: 'Dog',
      breeds: { primary: breedPreference?.breed || 'Golden Retriever' },
      age: 'Adult',
      gender: 'Male',
      size: 'Large',
      status: 'adoptable',
      description: 'A friendly and energetic dog looking for a loving home.',
      photos: [{ medium: 'https://images.unsplash.com/photo-1552053831-71594a27632d?w=300&h=300&fit=crop' }],
      contact: {
        email: 'demo@shelter.com',
        phone: '(555) 123-4567',
        address: { city: 'Demo City', state: 'DC', postcode: '12345' }
      },
      attributes: {
        spayed_neutered: true,
        house_trained: true,
        good_with_children: true,
        good_with_dogs: true,
        good_with_cats: false
      },
      compatibilityScore: 85
    },
    {
      id: 'demo-2',
      name: 'Luna',
      type: breedPreference?.animalType || 'cat',
      species: breedPreference?.animalType === 'cat' ? 'Cat' : 'Dog',
      breeds: { primary: breedPreference?.breed || 'Siamese' },
      age: 'Young',
      gender: 'Female',
      size: 'Medium',
      status: 'adoptable',
      description: 'A gentle and affectionate pet perfect for families.',
      photos: [{ medium: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=300&h=300&fit=crop' }],
      contact: {
        email: 'demo@shelter.com',
        phone: '(555) 123-4567',
        address: { city: 'Demo City', state: 'DC', postcode: '12345' }
      },
      attributes: {
        spayed_neutered: true,
        house_trained: true,
        good_with_children: true,
        good_with_dogs: false,
        good_with_cats: true
      },
      compatibilityScore: 78
    }
  ];

  return basePets;
};

/**
 * Custom hook for searching adoptable pets using Petfinder API
 * Integrates user's quiz preferences with location to find real pets
 */
export const usePetSearch = (quizResponses, breedPreference = null, initialLocation = null) => {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(initialLocation);
  const [hasSearched, setHasSearched] = useState(false);

  // Load saved location if not provided
  useEffect(() => {
    if (!location) {
      const savedLocation = loadLocationPreferences();
      if (savedLocation) {
        setLocation(savedLocation);
      }
    }
  }, [location]);

  // Automatically search when we have both quiz responses and location
  useEffect(() => {
    const shouldSearch = quizResponses && 
                        Object.keys(quizResponses).length > 0 && 
                        location && 
                        location.zipCode && 
                        !hasSearched;
    
    if (shouldSearch) {
      searchForPets();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizResponses, location, hasSearched, breedPreference]); // searchForPets excluded to avoid infinite loop

  const searchForPets = async (customLocation = null, options = {}) => {
    const searchLocation = customLocation || location;
    
    if (!searchLocation || !searchLocation.zipCode) {
      setError('Location is required to search for pets.');
      return;
    }

    if (!quizResponses || Object.keys(quizResponses).length === 0) {
      setError('Quiz responses are required to match pets.');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      // Merge breed preference with options if provided
      const searchOptions = breedPreference 
        ? { ...options, breedPreference }
        : options;
        
      const result = await searchPets(quizResponses, searchLocation, searchOptions);
      setPets(result.pets || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Pet search error:', err);
      
      // Check if it's an API credentials issue
      if (err.message.includes('credentials not configured') || err.message.includes('OAuth error')) {
        setError('Petfinder API not configured. Using demo data for now. To see real pets, please configure your API credentials in the .env file.');
        // Generate some demo pets based on preferences
        setPets(generateDemoPets(quizResponses, breedPreference));
      } else {
        setError(err.message || 'Failed to search for pets. Please try again.');
        setPets([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateLocation = (newLocation) => {
    setLocation(newLocation);
    setHasSearched(false); // Reset to trigger new search
  };

  const refreshSearch = () => {
    searchForPets();
  };

  const clearResults = () => {
    setPets([]);
    setError(null);
    setHasSearched(false);
  };

  return {
    pets,
    loading,
    error,
    location,
    hasSearched,
    searchForPets,
    updateLocation,
    refreshSearch,
    clearResults,
  };
};